import { useState } from "react";
import { runValidation } from "../../../services/form-validator";
import { useMutationWithDispatch } from "../../../hooks/useMutationWithDispatch";

function useForm({ formFieldConfig, formFields }) {
  const [formInputs, setFormInputs] = useState(formFields);
  const [fieldConfig, setFormConfig] = useState(formFieldConfig);
  const { mutateAsync: submitRequest, isPending } = useMutationWithDispatch<
    any,
    any
  >(
    (thunk: any) => thunk
  );

  function getCustomizedRequestPayload(customPayload = {}) {
    let payload = {};
    Object.keys(formInputs)
      .filter((item) => !fieldConfig.nonRequiredPayloadFields?.includes(item))
      .forEach((input) => {
        payload = {
          ...payload,
          [input]: formInputs[input],
        };
      });

    return {
      ...payload,
      ...customPayload,
    };
  }

  const setFormData = (data) => {
    let obj = { ...formInputs };

    Object.keys(formInputs).map((inputKey) => {
      obj = {
        ...obj,
        ...(data?.hasOwnProperty(inputKey) && { [inputKey]: data?.[inputKey] }),
      };
    });

    setFormInputs(obj);
  };

  const updateFieldConfig = (updatedFormConfig) => {
    setFormConfig(updatedFormConfig);
  };

  const handleInputChange = (e) => {
    e.persist && e.persist();

    setFormInputs((formInputs) => ({
      ...formInputs,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCheckedChange = (e) => {
    e.persist && e.persist();

    setFormInputs((formInputs) => ({
      ...formInputs,
      [e.target.name]: e.target.checked,
    }));
  };

  const handleSubmit: any = async (
    e,
    fn = () => {},
    successFn = (payload) => {},
    errorFn = () => {}
  ) => {
    e?.preventDefault();
    const formData = runValidation(fieldConfig, formInputs);

    if (!formData.isValid) {
      setFormConfig(formData);
      return;
    }

    try {
      const response: any = await submitRequest(fn);

      if (!response?.success) {
        errorFn?.();
        return;
      }

      formFieldConfig?.resetAfterSubmit && setFormInputs(formFields);
      successFn?.(response?.data);
    } catch {
      errorFn?.();
    } finally {
      setFormConfig(formData);
    }
  };

  const runValidator = (e, cb) => {
    e?.preventDefault();
    const formData = runValidation(fieldConfig, formInputs);

    cb(formData);

    setFormConfig(formData);
  };

  const validateInput = (fields, cb) => {
    const formData = runValidation(fieldConfig, fields);

    cb(formData);

    setFormConfig(formData);
  };

  const handleInputFocus = (e) => {
    e.persist && e.persist();

    setFormConfig({
      ...fieldConfig,
      fields: {
        ...fieldConfig.fields,
        [e.target.name]: {
          ...fieldConfig?.fields?.[e.target.name],
          error: "",
        },
      },
    });
  };

  return {
    handleSubmit,
    handleInputChange,
    getCustomizedRequestPayload,
    updateFieldConfig,
    handleCheckedChange,
    setFormData,
    runValidator,
    formInputs,
    fieldConfig,
    submitting: isPending,
    setFormConfig,
    handleInputFocus,
    validateInput,
  };
}

export { useForm };
